import React, { useState } from "react";
import { CheckCircle2, Upload, FileText, AlertTriangle } from "lucide-react";
import { applyResponse, previewResponse } from "./bankResponseApi";
import { bankApiError } from "./bankErrorUtils";

const BANK_CODES = [
  "HDFC",
  "ICICI",
  "SBI",
  "AXIS",
  "KOTAK",
  "YES",
  "INDUSIND",
  "IDFC_FIRST",
  "PNB",
  "BOB",
  "CANARA",
  "UNION",
  "INDIAN_BANK",
  "FEDERAL",
  "SIB",
  "RBL",
  "AU_SFB",
  "BANDHAN",
  "DEFAULT",
];

/**
 * Bank response file importer.
 *
 * Props: batchId (required).
 *
 * Flow: pick bank -> upload file -> preview (server parses + resolves matches) -> apply.
 */
export default function BankResponseImport({ batchId }) {
  const [bankCode, setBankCode] = useState("HDFC");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const doPreview = async () => {
    if (!file) {
      setError("Choose a file first.");
      return;
    }
    setLoading(true);
    setError("");
    setPreview(null);
    setApplyResult(null);
    try {
      const data = await previewResponse(batchId, { bankCode, file });
      setPreview(data);
    } catch (e) {
      setError(bankApiError(e) ||"Failed to preview response");
    } finally {
      setLoading(false);
    }
  };

  const doApply = async () => {
    if (
      !window.confirm(
        `Apply ${preview?.stats?.matched ?? 0} status updates to this batch? This cannot be automatically reverted.`
      )
    )
      return;
    setLoading(true);
    setError("");
    try {
      const data = await applyResponse(batchId, { bankCode, file });
      setApplyResult(data);
    } catch (e) {
      setError(bankApiError(e) ||"Failed to apply response");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Upload size={18} color="#1e40af" />
        <h2 style={{ margin: 0 }}>Import Bank Response</h2>
      </div>
      <div style={{ marginTop: 6, color: "#64748b", fontSize: 13 }}>
        Upload the reply file received from the bank after a bulk payout run. The parser
        matches each row to a salary batch item and updates its payment status.
      </div>

      {error && (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            background: "#fee2e2",
            color: "#991b1b",
            borderRadius: 6,
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          marginTop: 16,
          display: "flex",
          gap: 8,
          alignItems: "end",
          flexWrap: "wrap",
        }}
      >
        <label>
          <div style={{ fontSize: 12, color: "#64748b" }}>Bank</div>
          <select
            value={bankCode}
            onChange={(e) => setBankCode(e.target.value)}
            style={{ padding: 6 }}
          >
            {BANK_CODES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label>
          <div style={{ fontSize: 12, color: "#64748b" }}>Response file</div>
          <input
            type="file"
            accept=".csv,.txt,.dat"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            style={{ padding: 6 }}
          />
        </label>
        <button
          onClick={doPreview}
          disabled={!file || loading}
          style={{
            padding: "8px 12px",
            background: "#2563eb",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <FileText size={14} />
          {loading ? "Working…" : "Preview"}
        </button>
        {preview && (
          <button
            onClick={doApply}
            disabled={loading || preview?.stats?.matched === 0}
            style={{
              padding: "8px 12px",
              background: "#166534",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <CheckCircle2 size={14} />
            Apply
          </button>
        )}
      </div>

      {preview && (
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              display: "flex",
              gap: 12,
              padding: 10,
              background: "#f8fafc",
              borderRadius: 6,
            }}
          >
            <div>
              <div style={{ fontSize: 11, color: "#64748b" }}>Rows</div>
              <div style={{ fontFamily: "monospace" }}>{preview.parse.row_count}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#64748b" }}>Success</div>
              <div style={{ fontFamily: "monospace", color: "#166534" }}>
                {preview.parse.success_count}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#64748b" }}>Failed</div>
              <div style={{ fontFamily: "monospace", color: "#991b1b" }}>
                {preview.parse.failure_count}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#64748b" }}>Matched</div>
              <div style={{ fontFamily: "monospace" }}>
                {preview.stats?.matched ?? 0}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#64748b" }}>Unresolved</div>
              <div style={{ fontFamily: "monospace", color: "#b45309" }}>
                {preview.stats?.unresolved ?? 0}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#64748b" }}>Ambiguous</div>
              <div style={{ fontFamily: "monospace", color: "#b45309" }}>
                {preview.stats?.ambiguous ?? 0}
              </div>
            </div>
          </div>

          <table
            style={{
              width: "100%",
              marginTop: 12,
              borderCollapse: "collapse",
              fontSize: 12,
            }}
          >
            <thead>
              <tr style={{ background: "#f1f5f9", textAlign: "left" }}>
                <th style={{ padding: 6 }}>Status</th>
                <th style={{ padding: 6 }}>Payment Ref</th>
                <th style={{ padding: 6 }}>Acct (last4)</th>
                <th style={{ padding: 6 }}>UTR</th>
                <th style={{ padding: 6 }}>Reason</th>
                <th style={{ padding: 6 }}>Match</th>
              </tr>
            </thead>
            <tbody>
              {(preview.rows || []).map((r, i) => (
                <tr
                  key={i}
                  style={{
                    borderTop: "1px solid #e2e8f0",
                    background: r.error ? "#fef3c7" : "transparent",
                  }}
                >
                  <td style={{ padding: 6 }}>
                    <StatusChip status={r.to_status} />
                  </td>
                  <td style={{ padding: 6, fontFamily: "monospace" }}>
                    {r.payment_ref || "—"}
                  </td>
                  <td style={{ padding: 6, fontFamily: "monospace" }}>
                    {r.account_number_last4 || "—"}
                  </td>
                  <td style={{ padding: 6, fontFamily: "monospace" }}>
                    {r.payout_utr || "—"}
                  </td>
                  <td style={{ padding: 6 }}>{r.failure_reason || "—"}</td>
                  <td style={{ padding: 6 }}>
                    {r.matched_item_id ? (
                      <span style={{ color: "#166534" }}>
                        {["", "ref", "last4", "acct+ifsc"][r.match_score]}
                      </span>
                    ) : (
                      <span
                        style={{
                          color: "#b45309",
                          display: "inline-flex",
                          gap: 3,
                          alignItems: "center",
                        }}
                      >
                        <AlertTriangle size={11} /> {r.error || "unresolved"}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {applyResult && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            background: "#dcfce7",
            color: "#166534",
            borderRadius: 6,
          }}
        >
          Applied. Applied {applyResult.applied}, skipped {applyResult.skipped_no_change}.
          Response file id: {applyResult.response_file_id}
        </div>
      )}
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    success: { bg: "#dcfce7", fg: "#166534" },
    failed: { bg: "#fee2e2", fg: "#991b1b" },
    held: { bg: "#fef3c7", fg: "#854d0e" },
    pending: { bg: "#e2e8f0", fg: "#334155" },
  };
  const spec = map[status] || map.pending;
  return (
    <span
      style={{
        padding: "2px 6px",
        borderRadius: 999,
        background: spec.bg,
        color: spec.fg,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {status}
    </span>
  );
}
